/**
 * UmiJS 通用配置工厂
 */

/**
 * UmiJS 配置返回类型
 *
 * 列出工厂函数可能输出的已知字段，同时允许 UmiJS 插件扩展的未知字段。
 */
export interface UmiBaseConfig {
  routes: Record<string, unknown>[];
  hash: boolean;
  history: { type: 'hash' | 'browser' };
  publicPath: string;
  model: Record<string, unknown>;
  initialState: Record<string, unknown>;
  access: Record<string, unknown>;
  request: Record<string, unknown>;
  mako: Record<string, unknown>;
  fastRefresh: boolean;
  esbuildMinifyIIFE: boolean;
  ignoreMomentLocale: boolean;
  moment2dayjs: { preset: string; plugins: string[] };
  antd: Record<string, unknown>;
  locale?: false | { default: string; antd: boolean; baseNavigator: boolean };
  layout?: Record<string, unknown>;
  title?: string;
  alias?: Record<string, string>;
  define?: Record<string, unknown>;
  proxy?: Record<string, unknown>;
  codeSplitting?: { jsStrategy: string };
  headScripts?: Record<string, unknown>[];
  favicons?: string[];
  [key: string]: unknown;
}

/**
 * UmiJS 基础配置选项
 */
export interface UmiBaseConfigOptions {
  /** 路由配置 */
  routes: Record<string, unknown>[];
  /** 国际化配置，传 false 关闭 */
  locale?:
    | false
    | {
        default: string;
        antd?: boolean;
        baseNavigator?: boolean;
      };
  /** 公共路径，默认 '/' */
  publicPath?: string;
  /** 路径别名 */
  alias?: Record<string, string>;
  /** 环境变量定义 */
  define?: Record<string, unknown>;
  /** 代理配置 */
  proxy?: Record<string, unknown>;
  /** 应用标题 */
  title?: string;
  /** 是否使用 ProLayout，默认 false */
  useProLayout?: boolean;
  /** ProLayout 配置 */
  proLayoutSettings?: Record<string, unknown>;
  /** 是否使用 hash 路由，默认 true */
  hashRouting?: boolean;
  /** 是否启用代码分割，默认 false */
  codeSplitting?: boolean;
  /** head 中的额外脚本 */
  headScripts?: Record<string, unknown>[];
  /** 网站图标 */
  favicons?: string[];
  /** antd 主题配置 */
  antdTheme?: Record<string, unknown>;
}

/**
 * 创建 UmiJS 基础配置
 *
 * 输出统一的:
 * - hash routing
 * - model + initialState + access 插件
 * - mako 构建
 * - fastRefresh 热更新
 * - moment2dayjs
 * - antd CSS 变量模式
 * - esbuildMinifyIIFE
 *
 * @param options - 配置选项
 * @returns UmiJS 配置对象
 *
 * @example
 * ```ts
 * // config/config.ts
 * import { createUmiBaseConfig } from '@aspect/shared/config';
 * import routes from './routes';
 *
 * export default createUmiBaseConfig({
 *   routes,
 *   locale: { default: 'zh-CN', antd: true },
 *   title: 'My App',
 *   define: {
 *     'process.env.API_BASE_URL': 'https://api.example.com',
 *   },
 * });
 * ```
 */
export function createUmiBaseConfig(
  options: UmiBaseConfigOptions,
): UmiBaseConfig {
  const {
    routes,
    locale,
    publicPath = '/',
    alias,
    define,
    proxy,
    title,
    useProLayout = false,
    proLayoutSettings,
    hashRouting = true,
    codeSplitting = false,
    headScripts,
    favicons,
    antdTheme,
  } = options;

  const config: UmiBaseConfig = {
    // ===== 路由 =====
    routes,
    hash: hashRouting,
    history: { type: hashRouting ? 'hash' : 'browser' },
    publicPath,

    // ===== 插件 =====
    model: {},
    initialState: {},
    access: {},
    request: {},

    // ===== 构建优化 =====
    mako: {},
    fastRefresh: true,
    esbuildMinifyIIFE: true,
    ignoreMomentLocale: true,

    // ===== moment -> dayjs =====
    moment2dayjs: {
      preset: 'antd',
      plugins: ['duration'],
    },

    // ===== Ant Design =====
    antd: {
      import: false,
      appConfig: {},
      configProvider: {
        theme: {
          cssVar: true,
          ...(antdTheme || {}),
        },
      },
    },
  };

  // 国际化
  if (locale === false) {
    config.locale = false;
  } else if (locale) {
    config.locale = {
      default: locale.default,
      antd: locale.antd ?? true,
      baseNavigator: locale.baseNavigator ?? false,
    };
  }

  // ProLayout
  if (useProLayout) {
    config.layout = {
      locale: locale !== false,
      ...(proLayoutSettings || {}),
    };
  }

  // 标题
  if (title) {
    config.title = title;
  }

  // 别名
  if (alias) {
    config.alias = alias;
  }

  // 环境变量
  if (define) {
    config.define = define;
  }

  // 代理
  if (proxy) {
    config.proxy = proxy;
  }

  // 代码分割
  if (codeSplitting) {
    config.codeSplitting = {
      jsStrategy: 'granularChunks',
    };
  }

  // Head Scripts
  if (headScripts) {
    config.headScripts = headScripts;
  }

  // Favicons
  if (favicons) {
    config.favicons = favicons;
  }

  return config;
}
